import React from 'react'
import Link from 'next/link'
import SwiperCore, { Navigation, Pagination, EffectFade } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import { breakpoint } from '../layout/styles'
import { Container, Item, Content, Image } from '../styles/Banner'
import Cta from '../styles/Cta'
import { useMediaQuery } from '../helpers/mq'
import ReactHtmlParser from 'react-html-parser'
import styled, { css } from 'styled-components'
import getConfig from 'next/config'
import { useRouter } from 'next/router'

function formatTitle(title: string) {
  let newTitle = title.replace('*', '<strong>')
  newTitle = newTitle.replace('*', '</strong>')
  return newTitle
}

const Result = ({ item, full, autoPlay }: any) => {
  const isMobile = useMediaQuery(breakpoint.tablet_down);
  const image = isMobile && item.mobile_image ? item.mobile_image : item.image;
  const router = useRouter();
  const { publicRuntimeConfig } = getConfig();

  const textStyleLeft = item.text_alignment === 'Left' || item.text_alignment === 'Left Desktop - Top Mobile';
  const darkText = item.text_style === 'Dark - Dark Text';

  return (
    <CarouselItem
      cover={!item.mp4 && !item.webm ? item.image?.url : ``}
      full={full}
      light={item.text_style === 'Light'}
      variant='large'
      videoAboveContentOnMobile={
        publicRuntimeConfig.videoAboveContentOnMobile &&
        publicRuntimeConfig.videoAboveContentOnMobile.pages &&
        publicRuntimeConfig.videoAboveContentOnMobile.pages.includes(router.asPath)
      }
      overlay={item.overlay}
      left={textStyleLeft}
      center={item.text_alignment === 'Center'}
      style={{ height: '100%' }}>
      {item.mp4 || item.webm ? (
        <video autoPlay={autoPlay} muted loop playsInline poster={item.image && item.image.url}>
          {item.webm && <source src={item.webm.url} type='video/webm' />}
          {item.mp4 && <source src={item.mp4.url} type='video/mp4' />}
        </video>
      ) : (
        <CarouselBannerImage
          bg={image?.url}
          bgMobile={item.mobile_image?.url}
          margin={'0px'}
          left={textStyleLeft}
          leftDTopM={item.text_alignment === 'Left Desktop - Top Mobile'}
          style={{ height: '100%', maxHeight: '100%' }}
        />
      )}

      {(item.title || item.teaser || item.cta.href) && (
        <Container
          left={textStyleLeft}
          center={item.text_alignment === 'Center'}
          leftDTopM={item.text_alignment === 'Left Desktop - Top Mobile'}
          variant='large'
          video={!!(item.mp4 || item.webm)}>
          <Content
            variant='large'
            large_title={item.large_title}
            center={item.text_alignment === 'Center'}
            light={item.text_style === 'Light'}>
            <BannerContent
              center={item.text_alignment === 'Center'}
              leftDTopM={item.text_alignment === 'Left Desktop - Top Mobile'}
              darkText={darkText}
            >
              <h2 dangerouslySetInnerHTML={{ __html: formatTitle(item.title) }} />
              {item.teaser && (
                <p>
                  <b>{item.teaser}</b>
                </p>
              )}
              <p>{ReactHtmlParser(item.description)}</p>
              <CarouselCTAWrapper>
              {item.cta.href && (
                <Link href={item.cta.href} passHref>
                  <Cta href=''
                       target={item.cta_one_target}
                       light={item.text_style === 'Light'}
                       blue={item.text_style === 'Blue'}
                       ocean={item.text_style === 'Ocean'}
                       dark={item.text_style === 'Dark' || darkText}
                       warm_orange={item.text_style === 'Warm Orange'}>
                    {item.cta.title}
                  </Cta>
                </Link>
              )}
              {item.cta_two && item.cta_two.href && (
                <Link href={item.cta_two.href} passHref>
                  <Cta href=''
                       light={item.text_style === 'Light'}
                       blue={item.text_style === 'Blue'}
                       warm_orange={item.text_style === 'Warm Orange'}
                       second={true}>
                    {item.cta_two.title}
                  </Cta>
                </Link>
              )}
              </CarouselCTAWrapper>
            </BannerContent>
          </Content>
        </Container>
      )}
    </CarouselItem>
  )
}

SwiperCore.use([Navigation, Pagination, EffectFade])

const CarouselBanner = ({ items, full }: any) => {
  const desktop = useMediaQuery(breakpoint.desktop)
  const safeItems: any[] = items ?? []

  if (safeItems.length === 0) return null
  if (safeItems.length === 1) return <Result item={safeItems[0]} full={full} autoPlay={true} />

  const slideActions = (swiper: any) => {
    swiper.slides.forEach((el: any) => {
      el.querySelector('video')?.pause()
    })
    swiper.slides[swiper.activeIndex]?.querySelector('video')?.play()
    swiper.el.classList.toggle('swiper--light', swiper.slides[swiper.activeIndex].getAttribute('data-light') === 'true')
  }

  return (
    <CarouselContent>
      <Swiper
        navigation={desktop}
        slidesPerView={1}
        effect={desktop ? 'fade' : 'slide'}
        fadeEffect={{ crossFade: true }}
        onInit={(swiper: any) => {
          setTimeout(() => {
            swiper.el.classList.toggle(
              'swiper--light',
              swiper.slides[swiper.activeIndex].getAttribute('data-light') === 'true'
            )
          }, 1)
        }}
        onSwiper={slideActions}
        onSlideChange={slideActions}
        pagination={{
          type: 'fraction',
          renderFraction: (currentClass, totalClass) =>
            `<span class="${currentClass}"></span> of <span class="${totalClass}"></span>`
        }}>
        {safeItems.map((item: any, index: number) => (
          <SwiperSlide
            key={item.title}
            data-light={item.text_style === 'Light'}
            style={{ backgroundColor: item.background_color || '', height: 'auto' }}>
            <Result {...{ item, full, autoPlay: index === 0 }} />
          </SwiperSlide>
        ))}
      </Swiper>
    </CarouselContent>
  )
}

export default CarouselBanner

const CarouselCTAWrapper = styled.div<any>`
  display: flex;
  flex-direction: column;

  @media ${breakpoint.tablet} {
    flex-direction: row;
  }
`

const CarouselContent = styled.div<any>`
  .swiper-slide {
    @media ${breakpoint.tablet} {
      width: 100%;
    }
  }
`

const BannerContent = styled.div<any>`
  @media ${breakpoint.desktop} {
    max-width: ${(props) => (props.center ? `760px` : `475px`)};
  }
  h2 {
    img {
      margin-top: 0.5rem;
      @media ${breakpoint.desktop} {
        margin-top: 1rem;
      }
    }
  }
  p {
    white-space: pre-wrap;

    b {
      font-family: 'GothamBold', 'Open Sans', 'sans-serif';
    }
  }
  ${(props) =>
    props.leftDTopM &&
    css`
      @media ${breakpoint.tablet_down} {
        h2, p {
          color: var(--white)
        }
      }
  `}
  ${(props) =>
    props.darkText &&
    css`
      @media ${breakpoint.desktop} {
        color: var(--black);
      }
  `}
`

const CarouselItem = styled(Item)`
  @media ${breakpoint.desktop} {
    height: auto !important;
    aspect-ratio: 15 / 7;
    min-height: 20vh;
  }
`

export const CarouselBannerImage = styled(Image)`
  background: url('${(props) => props.bg}?height=672&auto=webp&format=pjpg&quality=75')
    no-repeat scroll center center;
  background-size: cover;
  height: auto !important;
  max-height: none !important;
  min-height: unset;
  aspect-ratio: 1 / 1;

  @media ${breakpoint.tablet} {
    aspect-ratio: 15 / 7;
  }

  ${(props) =>
    props.leftDTopM &&
    css`
      @media ${breakpoint.desktop_down} {
        background: url('${props.bgMobile}?height=672&auto=webp&format=pjpg&quality=75')
          no-repeat scroll center center;
        min-height: 350px;
      }
      @media ${breakpoint.tablet_down} {
        order: 2;
        min-height: 350px;
      }
  `}
`
